const PinchZoom = (function () {
  "use strict";

  class Pointer {
    constructor(nativePointer) {
      this.id = -1;
      this.nativePointer = nativePointer;
      this.pageX = nativePointer.pageX;
      this.pageY = nativePointer.pageY;
      this.clientX = nativePointer.clientX;
      this.clientY = nativePointer.clientY;
      if (self.Touch && nativePointer instanceof Touch) {
        this.id = nativePointer.identifier;
      } else if (isPointerEvent(nativePointer)) {
        this.id = nativePointer.pointerId;
      }
    }
    getCoalesced() {
      if ("getCoalescedEvents" in this.nativePointer) {
        return this.nativePointer.getCoalescedEvents().map((p) => new Pointer(p));
      }
      return [this];
    }
  }

  const isPointerEvent = (event) => self.PointerEvent && event instanceof PointerEvent;
  const noop = () => {};

  class PointerTracker {
    constructor(_element, callbacks) {
      this._element = _element;
      this.startPointers = [];
      this.currentPointers = [];
      const { start = () => true, move = noop, end = noop } = callbacks;
      this._startCallback = start;
      this._moveCallback = move;
      this._endCallback = end;
      this._pointerStart = this._pointerStart.bind(this);
      this._touchStart = this._touchStart.bind(this);
      this._move = this._move.bind(this);
      this._smoothMove = smoothThrottle(this._move);
      this._triggerPointerEnd = this._triggerPointerEnd.bind(this);
      this._pointerEnd = this._pointerEnd.bind(this);
      this._touchEnd = this._touchEnd.bind(this);

      if (self.PointerEvent) {
        this._element.addEventListener("pointerdown", this._pointerStart);
      } else {
        this._element.addEventListener("touchstart", this._touchStart);
        this._element.addEventListener("touchmove", this._smoothMove);
        this._element.addEventListener("touchend", this._touchEnd);
      }
    }
    _triggerPointerStart(pointer, event) {
      if (!this._startCallback(pointer, event)) return false;
      this.currentPointers.push(pointer);
      this.startPointers.push(pointer);
      return true;
    }
    _pointerStart(event) {
      if (event.button !== 0) return;
      if (!this._triggerPointerStart(new Pointer(event), event)) return;
      if (isPointerEvent(event)) {
        this._element.setPointerCapture(event.pointerId);
        this._element.addEventListener("pointermove", this._smoothMove);
        this._element.addEventListener("pointerup", this._pointerEnd);
        this._element.addEventListener("pointerleave", this._pointerEnd);
        this._element.addEventListener("touchend", this._touchEnd);
      }
    }
    _touchStart(event) {
      for (const touch of Array.from(event.changedTouches)) {
        this._triggerPointerStart(new Pointer(touch), event);
      }
    }
    _move(event) {
      const previousPointers = [...this.currentPointers];
      const changedPointers =
        "changedTouches" in event ? Array.from(event.changedTouches).map((t) => new Pointer(t)) : [new Pointer(event)];
      const trackedChangedPointers = [];
      for (const pointer of changedPointers) {
        const index = this.currentPointers.findIndex((p) => p.id === pointer.id);
        if (index === -1) continue;
        trackedChangedPointers.push(pointer);
        this.currentPointers[index] = pointer;
      }
      if (trackedChangedPointers.length === 0) return;
      this._moveCallback(previousPointers, trackedChangedPointers, event);
    }
    _triggerPointerEnd(_pointer, _event) {
      // Reset state
      this.currentPointers = [];
      this.startPointers = [];

      this._endCallback(_pointer, _event);
      return true;
    }
    _pointerEnd(event) {
      if (!this._triggerPointerEnd(new Pointer(event), event)) return;
      if (this.currentPointers.length) return;
      this._element.removeEventListener("pointermove", this._smoothMove);
      this._element.removeEventListener("pointerup", this._pointerEnd);
      this._element.removeEventListener("pointerleave", this._pointerEnd);
      this._element.removeEventListener("touchend", this._touchEnd);
    }
    _touchEnd(event) {
      for (const touch of Array.from(event.changedTouches)) {
        this._triggerPointerEnd(new Pointer(touch), event);
      }
      this._element.removeEventListener("pointermove", this._smoothMove);
      this._element.addEventListener("touchmove", this._smoothMove);
      this._element.removeEventListener("touchend", this._touchEnd);
    }
  }

  // Variables
  const minScaleAttr = "min-scale";
  const maxScaleAttr = "max-scale";
  const MIN_SCALE = 0.01;
  const MAX_SCALE = 5;
  var css =
    "pinch-zoom {\n display: block;\n  overflow: hidden;\n cursor: zoom-in;\n user-select: none;\n touch-action: none;\n  --scale: 1;\n  --x: 0;\n  --y: 0;\n}\n\npinch-zoom > * {\n width: 100%; height: auto;\n transform: translate(var(--x), var(--y)) scale(var(--scale));\n transition: 0.08s linear transform;\n  transform-origin: 0px 0px;\n  will-change: transform;\n user-select: none;\n touch-action: none;\n}\n";
  styleInject(css);
  // Scoped utility function
  function styleInject(css, ref) {
    if (ref === void 0) ref = {};
    var insertAt = ref.insertAt;

    if (!css || typeof document === "undefined") {
      return;
    }

    var head = document.head || document.getElementsByTagName("head")[0];
    var style = document.createElement("style");
    style.type = "text/css";

    if (insertAt === "top") {
      if (head.firstChild) {
        head.insertBefore(style, head.firstChild);
      } else {
        head.appendChild(style);
      }
    } else {
      head.appendChild(style);
    }

    if (style.styleSheet) {
      style.styleSheet.cssText = css;
    } else {
      style.appendChild(document.createTextNode(css));
    }
  }
  function getDistance(a, b) {
    if (!b) return 0;
    return Math.sqrt((b.clientX - a.clientX) ** 2 + (b.clientY - a.clientY) ** 2);
  }
  function getMidpoint(a, b) {
    if (!b) return a;
    return {
      clientX: (a.clientX + b.clientX) / 2,
      clientY: (a.clientY + b.clientY) / 2,
    };
  }
  function getAbsoluteValue(value, max) {
    if (typeof value === "number") return value;
    if (value.trimRight().endsWith("%")) {
      return (max * parseFloat(value)) / 100;
    }
    return parseFloat(value);
  }
  let cachedSvg;
  function getSVG() {
    return cachedSvg || (cachedSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg"));
  }
  function createMatrix() {
    return getSVG().createSVGMatrix();
  }
  function createPoint() {
    return getSVG().createSVGPoint();
  }

  class PinchZoom extends HTMLElement {
    constructor() {
      super();
      this._onWheel = this._onWheel.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._transform = createMatrix();

      new MutationObserver(() => this._stageElChange()).observe(this, { childList: true });
      // Initialize new PointerTracker
      const pointerTracker = new PointerTracker(this, {
        start: (pointer, event) => {
          if (pointerTracker.currentPointers.length === 2 || !this._positioningEl) return false;
          event.preventDefault();
          return true;
        },
        move: (previousPointers) => {
          smoothThrottle(this._onPointerMove(previousPointers, pointerTracker.currentPointers));
        },
      });
      // Listeners
      this.addEventListener("wheel", (event) => {
        event.preventDefault();
        smoothThrottle(this._onWheel(event));
      });
      this.addEventListener("pointerup", (e) => {
        // Prevent the modal from closing when the user clicks the image.
        // This allows users to click and hold to move around the zoomed image
        // and view other areas.
        e.stopPropagation();
      });
    }
    static get observedAttributes() {
      return [minScaleAttr];
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (name === minScaleAttr) {
        if (this.scale < this.minScale) {
          this.setTransform({ scale: this.minScale });
        }
      }
    }
    connectedCallback() {
      this._stageElChange();
    }
    // Getters and Setters
    get minScale() {
      const attrValue = this.getAttribute(minScaleAttr);
      if (!attrValue) return MIN_SCALE;
      const value = parseFloat(attrValue);
      if (Number.isFinite(value)) return Math.max(MIN_SCALE, value);
      return MIN_SCALE;
    }
    get maxScale() {
      const attrValue = this.getAttribute(maxScaleAttr);
      if (!attrValue) return MAX_SCALE;
      const value = parseFloat(attrValue);
      if (Number.isFinite(value)) return Math.min(value, MAX_SCALE);
      return MAX_SCALE;
    }
    get x() {
      return this._transform.e;
    }
    get y() {
      return this._transform.f;
    }
    get scale() {
      return this._transform.a;
    }
    set minScale(value) {
      this.setAttribute(minScaleAttr, String(value));
    }
    // Public Methods
    scaleTo(scale, opts = {}) {
      let { originX = 0, originY = 0 } = opts;
      const { relativeTo = "content", allowChangeEvent = false } = opts;
      const relativeToEl = relativeTo === "content" ? this._positioningEl : this;
      if (!relativeToEl || !this._positioningEl) {
        this.setTransform({ scale, allowChangeEvent });
        return;
      }
      const rect = relativeToEl.getBoundingClientRect();
      originX = getAbsoluteValue(originX, rect.width);
      originY = getAbsoluteValue(originY, rect.height);
      if (relativeTo === "content") {
        originX += this.x;
        originY += this.y;
      } else {
        const currentRect = this._positioningEl.getBoundingClientRect();
        originX -= currentRect.left;
        originY -= currentRect.top;
      }
      this._applyChange({
        allowChangeEvent,
        originX,
        originY,
        scaleDiff: scale / this.scale,
      });
    }
    setTransform(opts = {}) {
      const { scale = this.scale, allowChangeEvent = false } = opts;
      let { x = this.x, y = this.y } = opts;
      if (!this._positioningEl) {
        this._updateTransform(scale, x, y, allowChangeEvent);
        return;
      }
      const thisBounds = this.getBoundingClientRect();
      const positioningElBounds = this._positioningEl.getBoundingClientRect();
      if (!thisBounds.width || !thisBounds.height) {
        this._updateTransform(scale, x, y, allowChangeEvent);
        return;
      }
      let topLeft = createPoint();
      topLeft.x = positioningElBounds.left - thisBounds.left;
      topLeft.y = positioningElBounds.top - thisBounds.top;
      let bottomRight = createPoint();
      bottomRight.x = positioningElBounds.width + topLeft.x;
      bottomRight.y = positioningElBounds.height + topLeft.y;
      const matrix = createMatrix().translate(x, y).scale(scale).multiply(this._transform.inverse());
      topLeft = topLeft.matrixTransform(matrix);
      bottomRight = bottomRight.matrixTransform(matrix);
      if (topLeft.x > thisBounds.width) {
        x += thisBounds.width - topLeft.x;
      } else if (bottomRight.x < 0) {
        x += -bottomRight.x;
      }
      if (topLeft.y > thisBounds.height) {
        y += thisBounds.height - topLeft.y;
      } else if (bottomRight.y < 0) {
        y += -bottomRight.y;
      }
      this._updateTransform(scale, x, y, allowChangeEvent);
    }
    // Private Methods
    _updateTransform(scale, x, y, allowChangeEvent) {
      const minScaleTolerance = 0.009;
      if (
        scale < this.minScale ||
        this._inRange(scale, this.minScale - minScaleTolerance, this.minScale + minScaleTolerance)
      )
        return this._resetPosition();
      if (
        scale > this.maxScale ||
        this._inRange(scale, this.maxScale - minScaleTolerance, this.maxScale + minScaleTolerance)
      )
        return;
      if (scale === this.scale && x === this.x && y === this.y) return;

      this._transform.e = x;
      this._transform.f = y;
      this._transform.d = this._transform.a = scale;
      this.style.setProperty("--x", this.x + "px");
      this.style.setProperty("--y", this.y + "px");
      this.style.setProperty("--scale", this.scale + "");
      if (allowChangeEvent) {
        const event = new Event("change", { bubbles: true });
        this.dispatchEvent(event);
      }
    }
    _resetPosition() {
      this._transform.e = 0;
      this._transform.f = 0;
      this._transform.d = this.minScale;
      this.style.setProperty("--x", 0 + "px");
      this.style.setProperty("--y", 0 + "px");
      this.style.setProperty("--scale", this.minScale + "");
    }
    _stageElChange() {
      ["[_stageElChange] private method called"];
      this._positioningEl = undefined;
      if (this.children.length === 0) return;
      this._positioningEl = this.children[0];
      if (this.children.length > 1) {
        console.warn("<pinch-zoom> must not have more than one child.");
      }
      this.setTransform({ allowChangeEvent: true });
    }
    _onWheel(event) {
      if (!this._positioningEl) return;

      const currentRect = this._positioningEl.getBoundingClientRect();
      let { deltaY } = event;
      const { ctrlKey, deltaMode } = event;
      if (deltaMode === 1) {
        deltaY *= 15;
      }
      const divisor = ctrlKey ? 100 : 300;
      const scaleDiff = 1 - deltaY / divisor;

      this._applyChange({
        scaleDiff,
        originX: event.clientX - currentRect.left,
        originY: event.clientY - currentRect.top,
        allowChangeEvent: true,
      });
    }
    _inRange(x, min, max) {
      // Ignore mobile devices
      if (navigator.maxTouchPoints > 0) return false;
      return (x - min) * (x - max) <= 0;
    }
    _onPointerMove(previousPointers, currentPointers) {
      if (!this._positioningEl) return;
      const resetThreshold = 0.1;
      if (this._inRange(this.scale, this.minScale - resetThreshold, this.minScale + resetThreshold))
        return this._resetPosition();
      const currentRect = this._positioningEl.getBoundingClientRect();
      const prevMidpoint = getMidpoint(previousPointers[0], previousPointers[1]);
      const newMidpoint = getMidpoint(currentPointers[0], currentPointers[1]);
      const originX = prevMidpoint.clientX - currentRect.left;
      const originY = prevMidpoint.clientY - currentRect.top;
      const prevDistance = getDistance(previousPointers[0], previousPointers[1]);
      const newDistance = getDistance(currentPointers[0], currentPointers[1]);
      const scaleDiff = prevDistance ? newDistance / prevDistance : 1;
      this._applyChange({
        originX,
        originY,
        scaleDiff,
        panX: newMidpoint.clientX - prevMidpoint.clientX,
        panY: newMidpoint.clientY - prevMidpoint.clientY,
        allowChangeEvent: true,
      });
    }
    _applyChange(opts = {}) {
      const { panX = 0, panY = 0, originX = 0, originY = 0, scaleDiff = 1, allowChangeEvent = false } = opts;
      const matrix = createMatrix()
        .translate(panX, panY)
        .translate(originX, originY)
        .translate(this.x, this.y)
        .scale(scaleDiff)
        .translate(-originX, -originY)
        .scale(this.scale);

      this.setTransform({
        allowChangeEvent,
        scale: matrix.a,
        x: matrix.e,
        y: matrix.f,
      });
    }
  }
  customElements.define("pinch-zoom", PinchZoom);
  return PinchZoom;
})();
